<script>
import {VclCode} from 'vue-content-loading';
import {OrbitSpinner} from 'epic-spinners'
import {get_acsrf_token} from "@/methods";
import {required, minLength, maxLength} from "vuelidate/lib/validators";
import {mapGetters} from "vuex";

function parseISO8601String(dateString) {
	var timebits = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})(?::([0-9]*)(\.[0-9]*)?)?(?:([+-])([0-9]{2})([0-9]{2}))?/;
	var m = timebits.exec(dateString);
	var resultDate;
	if (m) {
		var utcdate = Date.UTC(parseInt(m[1]),
				parseInt(m[2]) - 1, // months are zero-offset (!)
				parseInt(m[3]),
				parseInt(m[4]), parseInt(m[5]), // hh:mm
				(m[6] && parseInt(m[6]) || 0),  // optional seconds
				(m[7] && parseFloat(m[7]) * 1000) || 0); // optional fraction
		// utcdate is milliseconds since the epoch
		if (m[9] && m[10]) {
			var offsetMinutes = parseInt(m[9]) * 60 + parseInt(m[10]);
			utcdate += (m[8] === '+' ? -1 : +1) * offsetMinutes * 60000;
		}
		resultDate = new Date(utcdate);
	} else {
		resultDate = null;
	}
	return resultDate;
}

export default {
	components: {
		// eslint-disable-next-line vue/no-unused-components
		VclCode,
		OrbitSpinner
	},
	computed: {
		...mapGetters({
			getSubscriberStatus: 'getSubscriberStatus',
			getEPRStatus: 'getEPRStatus'
		}),
	},
	validations: {
		applicationForm: {
			name: {required, minLength: minLength(3), maxLength: maxLength(24)}
		}
	},
	props: {},
	methods: {
		handleError: function (error) {
			console.log(`[ERROR] ${error}`);
			this.error = true;
		},
		getData: function () {
			fetch(process.env.VUE_APP_ROOT_API + 'v1/api/applications', {credentials: 'include'})
			.then(response => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
				}
			})
			.then(data => {
				this.ready = true;
				this.applications = data.applications;
				this.requestCountAverage = data.request_count_average;
				this.requestCountMonth = data.request_count_month;
				this.requestWarningThreshold = data.request_warning_threshold;
				if(this.requestCountMonth > this.requestCountAverage) {
					this.percentageUsed = (this.requestCountMonth / this.requestWarningThreshold)*100;
					this.percentageUsedCSS = `width: ${this.percentageUsed}%;`;
				} else {
					this.percentageUsed = (this.requestCountAverage / this.requestWarningThreshold)*100;
					this.percentageUsedCSS = `width: ${this.percentageUsed}%;`;
				}

			})
			.catch(error => {
				this.handleError(error);
			});
		},
		async showApplicationWizard() {
			this.$refs.addApplicationModal.show();
		},
		async validateApplicationForm() {
			this.$v.applicationForm.$touch();
		},
		async createApplication() {
			this.$v.applicationForm.$touch();
			if (this.$v.applicationForm.$error) return;

			this.creationInProgress = true;
			await new Promise(resolve => setTimeout(resolve, 2000));
			try {
				let acsrf_token = await get_acsrf_token();
				let payload = {
					acsrf_token: acsrf_token,
					name: this.applicationForm.name
				};
				let response = await fetch(process.env.VUE_APP_ROOT_API + `v1/api/applications`, {
					method: 'POST',
					body: JSON.stringify(payload),
					credentials: 'include'
				});

				if (response.ok) {
					this.applicationForm = {
						name: null
					};
					this.$refs.addApplicationModal.hide();
					this.$swal({
						icon: 'success',
						text: 'Application deployed'
					});
					await this.getData();
				} else {
					if (response.status === 429) {
						this.$toast.warning('You have been rate limited');

					} else
						throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
				}
			} catch (error) {
				console.log(`[ERROR] ${error}`);
				this.$swal({
					icon: 'error',
					text: 'Could not create application'
				});
			}
			this.creationInProgress = false;
		}
	},
	created() {
		this.getData()
	},
	data() {
		return {
			ready: false,
			error: false,
			applications: [],
			submitted: false,
			applicationForm: {
				name: null
			},
			creationInProgress: false,
			requestCountAverage: 0,
			requestCountMonth: 0,
			requestWarningThreshold: 2000000,
			percentageUsed: 0,
			percentageUsedCSS: ''
		}
	}
};
</script>

<template>
	<div>
		<div class="page-title-box d-flex align-items-center justify-content-between">
			<h2 class="mb-0">
				Applications
			</h2>
			<div class="page-title-right bv-d-xs-down-none">
				<ol class="breadcrumb m-0">
					<button class="btn btn-primary btn-lg text-uppercase" :disabled="creationInProgress" v-on:click="showApplicationWizard()">
						<h5 class="mb-0">
							<orbit-spinner
									v-if="creationInProgress"
									:animation-duration="900"
									:size="18"
									class="align-middle d-inline-block"
							/>
							New application
						</h5>
					</button>
				</ol>
			</div>
		</div>
		<b-modal
				ref="addApplicationModal"
				title="Create application"
				title-class="font-18"
				hide-footer
				size="sm"
		>
			<div class="row justify-content-center">
				<div class="col-lg-12 col-sm-12">
					<div class="form-group">
						<label for="name">
							Application name
						</label>
						<input
								id="name"
								v-model="applicationForm.name"
								type="text"
								class="form-control"
								:class="{ 'is-invalid': $v.applicationForm.name.$error }"
								v-on:change="validateApplicationForm()"
								v-on:input="validateApplicationForm()"
						/>
						<div v-if="$v.applicationForm.name.$error" class="invalid-feedback">
							<span v-if="!$v.applicationForm.name.required">Required</span>
							<span v-if="!$v.applicationForm.name.minLength || !$v.applicationForm.name.maxLength">3 - 24 characters</span>
						</div>
					</div>
				</div>
			</div>
			<div class="row justify-content-center">
				<div class="col-lg-8 col-sm-12">
					<button class="btn btn-primary btn-block" v-on:click="createApplication()" :class="{'disabled': creationInProgress}">
						<orbit-spinner
								v-if="creationInProgress"
								:animation-duration="1200"
								:size="16"
								class="align-middle d-inline-block"
						/>
						Create
					</button>
				</div>
			</div>
		</b-modal>
		<template v-if="ready">
			<template v-if="applications.length === 0">
				<div class="row">
					<div class="col justify-content-center">
						<div class="info-component text-center align-middle mt-auto mb-auto">
							<div class="info-component-space">
								<i class="fad fa-empty-set info-component-icon"></i>
							</div>
							<div class="row" style="margin-top: 20px;">
								<div class="col-lg-12">
									<h3 v-b-tooltip.hover title="Wow such empty 👻">Nothing here yet</h3>
									<h5 class="text-muted">Start by creating a new application</h5>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<!-- <template v-if="!getEPRStatus() && (total_requests/2) > 1250000"> -->
				<template v-if="!getEPRStatus() && ( requestCountAverage >= (requestWarningThreshold/2) || requestCountMonth >= (requestWarningThreshold/2) )">
					<div class="row">
						<div class="col">
							<div class="alert alert-warning" role="alert">
								<h4 class="alert-heading">
									<i class="fad fa-exclamation-triangle"/> Approaching Traffic Limit
								</h4>
								<p>
									CFTools Cloud Data API usage falls under our fair use policy. Your application should not exceed a monthly request average of {{$n(requestWarningThreshold)}} requests, otherwise your application might get restricted.<br/>
									<br/>
									Should you require higher request counts please contact our support in order to raise this limit for your account.
								</p>
								<hr>
								<div class="mb-0">
									<div class="text-center">
										<h5 class="p-0 text-black">
											<template v-if="requestCountMonth > requestCountAverage">
												{{ $n(requestCountMonth) }} / {{$n(requestWarningThreshold)}}
											</template>
											<template v-else>
												{{ $n(requestCountAverage) }} / {{$n(requestWarningThreshold)}}
											</template>
										</h5>
									</div>
									<div class="progress" style="height: 20px;">
										<div
												class="progress-bar"
												:class="{
													'bg-danger': percentageUsed >= 80,
													'bg-warning': percentageUsed > 50 && percentageUsed < 80
												}"
												role="progressbar"
												:aria-valuenow="percentageUsed"
												aria-valuemin="0"
												:style="percentageUsedCSS"
										>
											<span class="h6 p-0 m-0">
												{{ percentageUsed.toFixed(2) }} %
											</span>
										</div>
									</div>
								</div>
							</div>

						</div>
					</div>
				</template>
				<div class="row">
					<div class="col-lg-3 col-sm-12 h-100" v-for="application in applications" :key="application.id">
						<router-link :to="{name: 'application', params: {application_id: application.id}}">
							<div class="card text-white h-100">
								<div class="card-body h-100">
									<div class="pull-left position-absolute">
										<div class="badge badge-danger" v-if="application.privileged">
											<h6 class="mb-0 text-black">
												<i class="fad fa-badge"></i>
												Privileged
											</h6>
										</div>
									</div>
									<div class="row justify-content-center">
										<div class="col text-center">
											<h3 class="pb-0 mt-5 mb-3 text-truncate">
												{{ application.name }}
											</h3>
										</div>
									</div>
								</div>
								<div class="card-footer">
									<div class="row text-center">
										<div class="col">
											<p class="text-muted text-truncate mb-2">
												Accessible resources
											</p>
											<h5 class="mb-0">
												{{ application.tokens }}
											</h5>
										</div>
										<div class="col">
											<p class="text-muted text-truncate mb-2">
												Requests This Month
											</p>
											<h5 class="mb-0">
												{{ application.statistics.requests.month_current }}
											</h5>
										</div>
									</div>
								</div>
							</div>
						</router-link>
					</div>
				</div>
			</template>
		</template>
		<template v-else-if="error">
			<div class="row">
				<div class="col-lg-12 col-sm-12 row justify-content-center">
					<div class="col-lg-6 col-sm-12">
						<div class="card border border-danger">
							<div class="card-header bg-transparent border-danger">
								<h5 class="my-0 text-danger">
									<i class="far fa-exclamation-circle mr-3"></i>
									We have encountered an error
								</h5>
							</div>
							<div class="card-body pt-0">
								<h5 class="card-title mt-0">Please try again later</h5>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="row">
				<div class="col-lg-3 col-sm-12" v-for="index in 5" :key="index">
					<div class="card">
						<div class="card-body">
							<vcl-code :width="300" :speed="1" primary="#FFFFFF" secondary="#bdbdbd"></vcl-code>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>

</template>
